import { Component, DestroyRef, type TemplateRef, inject, viewChild } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { MtButtonComponent } from '../../../design-system/button/mt-button.component';
import { LEGAL_VERSION } from '../../../shared/legal/legal-document';
import { loadLegalTexts } from '../../../shared/legal/load-legal-texts';
import { JourneyNavigation } from '../journey-navigation';
import { JourneyStore } from '../journey.store';
import { PublicJourneyConfig, PublicJourneyUseCases } from '../public-journey.dependencies';
import { refreshOnLanguageChange } from './screen-refresh';

/** A piece of the acceptance line: plain text, or one of the two links. */
interface AcceptPart {
  readonly link: 'terms' | 'privacy' | null;
  readonly text: string;
}

/** Where the two links sit in the translated line, before it is cut around them. */
const MARK = '§§';

/**
 * The first arrival, on the house green: what the app is, what it will never do with what it is
 * told, and one button. It is the first thing drawn, so it is part of the first download.
 */
@Component({
  selector: 'app-home-screen',
  standalone: true,
  imports: [MtButtonComponent],
  templateUrl: './home-screen.component.html',
  styleUrl: './home-screen.component.scss',
})
export class HomeScreenComponent {
  private readonly useCases = inject(PublicJourneyUseCases);
  private readonly config = inject(PublicJourneyConfig);
  private readonly i18n = inject(TranslocoService);
  private readonly store = inject(JourneyStore);
  private readonly navigation = inject(JourneyNavigation);
  /** Set once « J'ai compris » is pressed, so the notice goes at once. */
  private termsAcknowledged = false;
  private readonly footer = viewChild<TemplateRef<unknown>>('footer');

  constructor() {
    refreshOnLanguageChange();
    this.store.present(
      { view: 'home', title: () => '', footer: () => this.footer() },
      inject(DestroyRef),
    );
    // The terms and the policy, fetched while the reader reads this screen: once here, the two
    // links under the button open them without a network.
    const fetchTexts = (): void => void loadLegalTexts(this.i18n.getActiveLang()).catch(() => null);
    if (typeof requestIdleCallback === 'function')
      requestIdleCallback(fetchTexts, { timeout: 4000 });
    else setTimeout(fetchTexts, 2000);
  }

  protected get initializing(): boolean {
    return this.store.initializing();
  }

  /** In the apps, the first launch says where a will kept in a browser can come back from. */
  protected readonly nativeApp = this.config.nativeApp;

  protected get resumeAvailable(): boolean {
    return this.store.resumeAvailable();
  }

  /**
   * The terms changed since the owner last accepted them — or they never had the chance, having
   * started before there were any: said once, with the two texts a tap away.
   */
  protected get termsChanged(): boolean {
    if (this.termsAcknowledged || this.initializing || !this.resumeAvailable) return false;
    const accepted = this.config.legalLedger.accepted();
    return accepted === null || accepted.version < LEGAL_VERSION;
  }

  protected acknowledgeTerms(): void {
    this.config.legalLedger.accept(LEGAL_VERSION, new Date().toISOString());
    this.termsAcknowledged = true;
  }

  /**
   * « En continuant, tu acceptes les {{terms}} et la {{privacy}}. », cut around its two links:
   * one sentence per language, so each language puts them where its grammar wants them.
   */
  protected get acceptLine(): readonly AcceptPart[] {
    const line = this.i18n.translate('legal.acceptLine', {
      terms: `${MARK}terms${MARK}`,
      privacy: `${MARK}privacy${MARK}`,
    });
    return line
      .split(MARK)
      .filter((part) => part.length > 0)
      .map((part): AcceptPart =>
        part === 'terms' || part === 'privacy'
          ? { link: part, text: this.translate(`legal.${part}`) }
          : { link: null, text: part },
      );
  }

  protected openLegal(document: 'terms' | 'privacy'): void {
    this.navigation.openLegal(document);
  }

  /** A backup made on another phone, or before this one was reset, is brought back here. */
  protected restore(): void {
    this.store.go('restore');
  }

  protected get startLabel(): string {
    return this.translate(this.store.resumeAvailable() ? 'actions.resume' : 'actions.start');
  }

  /** A draft already here is carried on; otherwise one is made, and the questions begin. */
  protected async start(): Promise<void> {
    this.store.errorMessage.set('');
    if (this.store.resumeAvailable()) {
      this.store.enterDashboard();
      return;
    }
    // Continuing is accepting: the line under the button says so, with the two texts linked.
    this.config.legalLedger.accept(LEGAL_VERSION, new Date().toISOString());
    this.store.busy.set(true);
    try {
      // Before the first question shows: it opens on the country proposed.
      const residenceCountry = await this.config.proposeResidence();
      this.store.profile.set(Object.freeze({ ...this.store.profile(), residenceCountry }));
    } finally {
      this.store.go('profile');
    }
    try {
      this.store.draft.set(
        await this.useCases.createDraft.execute({
          id: this.config.draftId,
          owner: this.store.profile(),
          nextReviewAt: this.config.nextReviewAt,
        }),
      );
    } catch (error: unknown) {
      this.store.fail(error, this.translate('errors.unexpected'));
    } finally {
      this.store.busy.set(false);
    }
  }

  protected get text() {
    return {
      homeCredit: this.translate('home.credit'),
      changedTitle: this.translate('legal.changedTitle'),
      changedText: this.translate('legal.changedText'),
      privacy: this.translate('legal.privacy'),
      terms: this.translate('legal.terms'),
      understood: this.translate('legal.understood'),
      homeRestore: this.translate('home.restoreLink'),
      nativeRestore: this.translate('native.restoreHint'),
      homeEyebrow: this.translate('home.eyebrow'),
      homeLead: this.translate('home.lead'),
      homeTitle: this.translate('home.title'),
      trustText: this.translate('home.trustText'),
      trustPhrase: this.translate('home.trustPhrase'),
      trustTitle: this.translate('home.trustTitle'),
    };
  }

  private translate(key: string): string {
    return this.i18n.translate(key);
  }
}
